import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page, TextElement, ImageElement } from '../types/storybook';
import {
  STORY_BUBBLE_PADDING,
  STORY_TEXT_LINE_HEIGHT,
  STORY_TEXT_PADDING,
  resolveTextDirection,
  toCssFontFamily
} from '../utils/textTypography';
import {
  CanvasAlignment,
  CanvasElementGeometry,
  SmartGuide,
  alignCanvasElement,
  distributeCanvasElements,
  getImageAccessibleName,
  nudgeCanvasElement,
  snapCanvasElement
} from '../utils/canvasAssist';
import CanonicalPageRenderer from './CanonicalPageRenderer';
import './Canvas.css';

const SNAP_THRESHOLD_SCREEN_PIXELS = 6;
const KEYBOARD_NUDGE_STEP = 1;
const KEYBOARD_LARGE_NUDGE_STEP = 10;
const ALIGNMENT_CONTROLS: Array<{
  alignment: CanvasAlignment;
  testId: string;
  label: string;
  icon: string;
}> = [
  { alignment: 'left', testId: 'align-left', label: 'Align selected element to page left', icon: '⇤' },
  { alignment: 'center', testId: 'align-center', label: 'Align selected element to page horizontal center', icon: '↔' },
  { alignment: 'right', testId: 'align-right', label: 'Align selected element to page right', icon: '⇥' },
  { alignment: 'top', testId: 'align-top', label: 'Align selected element to page top', icon: '↥' },
  { alignment: 'middle', testId: 'align-middle', label: 'Align selected element to page vertical middle', icon: '↕' },
  { alignment: 'bottom', testId: 'align-bottom', label: 'Align selected element to page bottom', icon: '↧' }
];

const CANVAS_SIZES = [
  { value: '5x8', width: 500, height: 800 },
  { value: '5.06x7.81', width: 506, height: 781 },
  { value: '5.25x8', width: 525, height: 800 },
  { value: '5.5x8.5', width: 550, height: 850 },
  { value: '6x9', width: 600, height: 900 },
  { value: '6.14x9.21', width: 614, height: 921 },
  { value: '6.69x9.61', width: 669, height: 961 },
  { value: '7x10', width: 700, height: 1000 },
  { value: '7.44x9.69', width: 744, height: 969 },
  { value: '7.5x9.25', width: 750, height: 925 },
  { value: '8x10', width: 800, height: 1000 },
  { value: '8.25x6', width: 825, height: 600 },
  { value: '8.25x8.25', width: 825, height: 825 },
  { value: '8.5x8.5', width: 850, height: 850 },
  { value: '8.5x11', width: 850, height: 1100 },
  { value: '8.27x11.69', width: 827, height: 1169 },
  { value: '5.5x8.5-hc', width: 550, height: 850 },
  { value: '6x9-hc', width: 600, height: 900 },
  { value: '6.14x9.21-hc', width: 614, height: 921 },
  { value: '7x10-hc', width: 700, height: 1000 },
  { value: '8.25x11-hc', width: 825, height: 1100 }
];

interface CanvasProps {
  page: Page;
  canvasSize: string;
  selectedElement: string | null;
  onSelectElement: (id: string | null) => void;
  onUpdateTextElement: (id: string, updates: Partial<TextElement>) => void;
  onUpdateImageElement: (id: string, updates: Partial<ImageElement>) => void;
  onUpdatePage: (updates: Partial<Page>) => void;
  onAddImage: (imageUrl: string, x: number, y: number, prompt?: string, characterName?: string) => void;
  onCopyElement?: (element: TextElement | ImageElement, type: 'text' | 'image') => void;
  onPasteElement?: (x: number, y: number) => void;
  onBringToFront?: (id: string, type: 'text' | 'image') => void;
  onSendToBack?: (id: string, type: 'text' | 'image') => void;
  showGrid?: boolean;
  showPreviousPageOverlay?: boolean;
  previousPage?: Page;
  previousPageOpacity?: number;
  safeMarginInches?: number;
  isPreviewMode?: boolean;
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';
type DragState = {
  id: string;
  type: 'text' | 'image';
  mode: 'move' | 'resize';
  handle?: ResizeHandle;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  aspectRatio: number;
  rotation: number;
};

const Canvas: React.FC<CanvasProps> = ({
  page,
  canvasSize,
  selectedElement,
  onSelectElement,
  onUpdateTextElement,
  onUpdateImageElement,
  onUpdatePage,
  onAddImage,
  onCopyElement,
  onPasteElement,
  showGrid = false,
  showPreviousPageOverlay = false,
  previousPage,
  previousPageOpacity = 0.3,
  safeMarginInches = 0.25,
  isPreviewMode = false
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<DragState | null>(null);
  const [aspectRatioLocked, setAspectRatioLocked] = useState<Record<string, boolean>>({});
  const [snappingEnabled, setSnappingEnabled] = useState(true);
  const [smartGuides, setSmartGuides] = useState<SmartGuide[]>([]);
  const [assistAnnouncement, setAssistAnnouncement] = useState('');
  const sizeConfig = useMemo(
    () => CANVAS_SIZES.find(size => size.value === canvasSize) || CANVAS_SIZES[3],
    [canvasSize]
  );
  const pageRef = useRef(page);
  const snappingEnabledRef = useRef(snappingEnabled);
  pageRef.current = page;
  snappingEnabledRef.current = snappingEnabled;
  const accessibleIdToken = useMemo(
    () => String(page.id || page.pageNumber).replace(/[^a-z0-9_-]/gi, '-'),
    [page.id, page.pageNumber]
  );
  const keyboardHelpId = `canvas-keyboard-help-${accessibleIdToken}`;
  const gridPatternId = `editor-grid-${accessibleIdToken}`;

  const canvasScale = useCallback(() => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 ? rect.width / sizeConfig.width : 1;
  }, [sizeConfig.width]);

  // The canonical renderer owns all printable visuals. While a text element is
  // actively edited, only its canonical glyph node is hidden; its exact
  // background, bubble, border, and transform remain visible underneath.
  useEffect(() => {
    const canonicalTexts = canvasRef.current?.querySelectorAll<HTMLElement>('.canonical-text-content');
    canonicalTexts?.forEach(node => {
      const owner = node.closest<HTMLElement>('.canonical-text');
      node.style.visibility = owner?.dataset.elementId === selectedElement ? 'hidden' : '';
    });
    return () => canonicalTexts?.forEach(node => {
      node.style.visibility = '';
    });
  }, [page, selectedElement]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const active = dragState.current;
      if (!active) return;
      const scale = Math.max(0.01, canvasScale());
      const deltaX = (event.clientX - active.startClientX) / scale;
      const deltaY = (event.clientY - active.startClientY) / scale;

      if (active.mode === 'move') {
        const proposedPosition = { x: active.startX + deltaX, y: active.startY + deltaY };
        const activeGeometry: CanvasElementGeometry = {
          id: active.id,
          x: active.startX,
          y: active.startY,
          width: active.startWidth,
          height: active.startHeight,
          rotation: active.rotation
        };
        const peers: CanvasElementGeometry[] = [
          ...(pageRef.current.imageElements || []),
          ...(pageRef.current.textElements || [])
        ];
        const snapResult = snappingEnabledRef.current
          ? snapCanvasElement(
            activeGeometry,
            proposedPosition,
            { width: sizeConfig.width, height: sizeConfig.height },
            peers,
            SNAP_THRESHOLD_SCREEN_PIXELS / scale
          )
          : { ...proposedPosition, guides: [] };
        const updates = { x: snapResult.x, y: snapResult.y };
        setSmartGuides(snapResult.guides);
        setAssistAnnouncement(snapResult.guides.map(guide => guide.label).join('. '));
        if (active.type === 'text') onUpdateTextElement(active.id, updates);
        else onUpdateImageElement(active.id, updates);
        return;
      }

      let width = active.startWidth;
      let height = active.startHeight;
      let x = active.startX;
      let y = active.startY;
      const minWidth = 50;
      const minHeight = active.type === 'text' ? 30 : 50;
      const horizontal = active.handle?.includes('e') ? deltaX : -deltaX;
      const vertical = active.handle?.includes('s') ? deltaY : -deltaY;
      width = Math.max(minWidth, active.startWidth + horizontal);
      height = Math.max(minHeight, active.startHeight + vertical);

      const keepRatio = active.type === 'image'
        && ((aspectRatioLocked[active.id] ?? true) || event.shiftKey);
      if (keepRatio) {
        const widthScale = width / active.startWidth;
        const heightScale = height / active.startHeight;
        const scaleFactor = Math.max(widthScale, heightScale);
        width = Math.max(minWidth, active.startWidth * scaleFactor);
        height = width / active.aspectRatio;
      }

      if (active.handle?.includes('w')) x = active.startX + active.startWidth - width;
      if (active.handle?.includes('n')) y = active.startY + active.startHeight - height;
      const updates = { x, y, width, height };
      if (active.type === 'text') onUpdateTextElement(active.id, updates);
      else onUpdateImageElement(active.id, updates);
    };
    const handleMouseUp = () => {
      dragState.current = null;
      setSmartGuides([]);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [aspectRatioLocked, canvasScale, onUpdateImageElement, onUpdateTextElement, sizeConfig.height, sizeConfig.width]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement
        && target.matches('input, textarea, select, button, a, [contenteditable="true"]')) return;
      const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'] as const;
      const arrowKey = arrowKeys.find(key => key === event.key);
      const targetIsInsideCanvas = target instanceof Node && Boolean(canvasRef.current?.contains(target));
      if (arrowKey && targetIsInsideCanvas && selectedElement
        && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const text = pageRef.current.textElements.find(element => element.id === selectedElement);
        const image = pageRef.current.imageElements.find(element => element.id === selectedElement);
        const selected = text || image;
        if (selected) {
          event.preventDefault();
          const step = event.shiftKey ? KEYBOARD_LARGE_NUDGE_STEP : KEYBOARD_NUDGE_STEP;
          const updates = nudgeCanvasElement(selected, arrowKey, step);
          if (text) onUpdateTextElement(text.id, updates);
          else if (image) onUpdateImageElement(image.id, updates);
          setSmartGuides([]);
          setAssistAnnouncement(`Moved ${arrowKey.replace('Arrow', '').toLowerCase()} ${step} canvas ${step === 1 ? 'unit' : 'units'}.`);
          return;
        }
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c' && selectedElement) {
        const text = pageRef.current.textElements.find(element => element.id === selectedElement);
        const image = pageRef.current.imageElements.find(element => element.id === selectedElement);
        if (text && onCopyElement) {
          event.preventDefault();
          onCopyElement(text, 'text');
        } else if (image && onCopyElement) {
          event.preventDefault();
          onCopyElement(image, 'image');
        }
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v' && onPasteElement) {
        event.preventDefault();
        onPasteElement(sizeConfig.width / 2, sizeConfig.height / 2);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCopyElement, onPasteElement, onUpdateImageElement, onUpdateTextElement, selectedElement, sizeConfig]);

  const startMove = (
    event: React.MouseEvent,
    element: TextElement | ImageElement,
    type: 'text' | 'image'
  ) => {
    // Shift + primary drag and middle-button drag belong to workspace panning.
    // Leave those events untouched so they can bubble to the canvas stage.
    if (event.button !== 0 || event.shiftKey) return;
    if ((event.target as HTMLElement).closest('textarea, button')) return;
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).focus({ preventScroll: true });
    onSelectElement(element.id);
    setSmartGuides([]);
    dragState.current = {
      id: element.id,
      type,
      mode: 'move',
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: element.x,
      startY: element.y,
      startWidth: element.width,
      startHeight: element.height,
      aspectRatio: element.width / element.height,
      rotation: element.rotation || 0
    };
  };

  const startResize = (
    event: React.MouseEvent,
    element: TextElement | ImageElement,
    type: 'text' | 'image',
    handle: ResizeHandle
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onSelectElement(element.id);
    setSmartGuides([]);
    if (type === 'image') {
      setAspectRatioLocked(current => ({ ...current, [element.id]: current[element.id] ?? true }));
    }
    dragState.current = {
      id: element.id,
      type,
      mode: 'resize',
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: element.x,
      startY: element.y,
      startWidth: element.width,
      startHeight: element.height,
      aspectRatio: element.width / element.height,
      rotation: element.rotation || 0
    };
  };

  const selectedCanvasElement = useMemo(() => {
    const text = page.textElements.find(element => element.id === selectedElement);
    if (text) return { element: text, type: 'text' as const };
    const image = page.imageElements.find(element => element.id === selectedElement);
    return image ? { element: image, type: 'image' as const } : null;
  }, [page.imageElements, page.textElements, selectedElement]);

  const alignSelectedElement = (alignment: CanvasAlignment) => {
    if (!selectedCanvasElement) return;
    const updates = alignCanvasElement(
      selectedCanvasElement.element,
      { width: sizeConfig.width, height: sizeConfig.height },
      alignment
    );
    if (selectedCanvasElement.type === 'text') {
      onUpdateTextElement(selectedCanvasElement.element.id, updates);
    } else {
      onUpdateImageElement(selectedCanvasElement.element.id, updates);
    }
    setSmartGuides([]);
    setAssistAnnouncement(`Aligned ${selectedCanvasElement.type} to page ${alignment}.`);
  };

  const pageElementCount = page.imageElements.length + page.textElements.length;
  const distributePageElements = (axis: 'horizontal' | 'vertical') => {
    if (pageElementCount < 3) return;
    const elements = [
      ...page.imageElements.map(element => ({ ...element, canvasElementType: 'image' as const })),
      ...page.textElements.map(element => ({ ...element, canvasElementType: 'text' as const }))
    ];
    const distributed = distributeCanvasElements(elements, axis);
    const distributedByKey = new Map(distributed.map(current => (
      [`${current.canvasElementType}:${current.id}`, current] as const
    )));
    const imageElements = page.imageElements.map(element => {
      const distributedElement = distributedByKey.get(`image:${element.id}`);
      if (!distributedElement) return element;
      const { canvasElementType: _canvasElementType, ...nextElement } = distributedElement;
      return nextElement as ImageElement;
    });
    const textElements = page.textElements.map(element => {
      const distributedElement = distributedByKey.get(`text:${element.id}`);
      if (!distributedElement) return element;
      const { canvasElementType: _canvasElementType, ...nextElement } = distributedElement;
      return nextElement as TextElement;
    });
    // Persist the entire distribution atomically. Per-element state updates can
    // otherwise overwrite one another when several interior elements move.
    onUpdatePage({ imageElements, textElements });
    setSmartGuides([]);
    setAssistAnnouncement(`Distributed all ${pageElementCount} page elements ${axis === 'horizontal' ? 'horizontally' : 'vertically'}.`);
  };

  const resizeHandles = (
    element: TextElement | ImageElement,
    type: 'text' | 'image'
  ) => selectedElement === element.id ? (
    <>
      {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map(handle => (
        <button
          key={handle}
          type="button"
          className={`resize-handle ${handle}`}
          aria-label={`Resize ${type} from ${handle.toUpperCase()} corner`}
          onMouseDown={event => startResize(event, element, type, handle)}
        />
      ))}
    </>
  ) : null;

  const interactionStyle = (element: TextElement | ImageElement): React.CSSProperties => ({
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    transform: `rotate(${element.rotation || 0}deg)`
  });

  const textEditor = (text: TextElement) => {
    if (selectedElement !== text.id) return null;
    const bubble = Boolean(text.styleId?.includes('bubble'));
    const padding = bubble ? STORY_BUBBLE_PADDING : STORY_TEXT_PADDING;
    return (
      <textarea
        className="canonical-text-editor"
        data-testid="canonical-text-editor"
        aria-label="Edit story text"
        value={text.content}
        dir={resolveTextDirection(text.content)}
        onChange={event => onUpdateTextElement(text.id, { content: event.target.value })}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
        onKeyDown={event => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.closest<HTMLElement>('.editor-interaction-element')?.focus();
          setAssistAnnouncement('Text editing finished. Arrow keys now move the text box.');
        }}
        style={{
          width: '100%',
          height: '100%',
          padding,
          border: 0,
          margin: 0,
          resize: 'none',
          overflow: 'hidden',
          boxSizing: 'border-box',
          background: 'transparent',
          color: text.color,
          fontFamily: toCssFontFamily(text.fontFamily, text.content),
          fontSize: text.fontSize,
          fontWeight: text.fontWeight,
          fontStyle: text.fontStyle || 'normal',
          textDecoration: text.textDecoration || 'none',
          textAlign: text.textAlign || 'center',
          lineHeight: STORY_TEXT_LINE_HEIGHT,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          textShadow: text.textShadow
            ? `${text.shadowOffsetX || 0}px ${text.shadowOffsetY || 0}px ${text.shadowBlur || 0}px ${text.shadowColor || '#000000'}`
            : 'none'
        }}
      />
    );
  };

  const selectionCount = selectedCanvasElement ? 1 : 0;
  const canDistribute = pageElementCount >= 3;
  const distributionHelp = canDistribute
    ? `Evenly space all ${pageElementCount} page elements while preserving the first and last center positions.`
    : `Distribution requires at least 3 page elements. This page has ${pageElementCount}.`;
  const safeMargin = Math.min(
    Math.max(0, safeMarginInches * 100),
    Math.min(sizeConfig.width, sizeConfig.height) / 2
  );

  return (
    <div
      ref={canvasRef}
      className="canvas-container"
      data-testid="canonical-page-editor"
      data-canvas-width={sizeConfig.width}
      data-canvas-height={sizeConfig.height}
      data-selected-element={selectedElement || ''}
      style={{ width: sizeConfig.width, height: sizeConfig.height }}
      role="region"
      aria-roledescription="storybook design canvas"
      aria-label={`Page ${page.pageNumber} design canvas`}
      aria-describedby={keyboardHelpId}
      onClick={event => {
        if (!(event.target as HTMLElement).closest('.editor-interaction-element, .canvas-assist-toolbar')) {
          onSelectElement(null);
        }
      }}
      onDragOver={event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={event => {
        event.preventDefault();
        const imageUrl = event.dataTransfer.getData('imageUrl');
        if (!imageUrl || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const scale = Math.max(0.01, rect.width / sizeConfig.width);
        onAddImage(
          imageUrl,
          (event.clientX - rect.left) / scale,
          (event.clientY - rect.top) / scale,
          event.dataTransfer.getData('prompt'),
          event.dataTransfer.getData('name')
        );
      }}
    >
      <div
        className="canvas-print-surface"
        aria-hidden={isPreviewMode ? undefined : true}
        role={isPreviewMode ? 'document' : undefined}
        aria-label={isPreviewMode ? `Preview of storybook page ${page.pageNumber}` : undefined}
      >
        <CanonicalPageRenderer
          page={page}
          canvasSize={canvasSize}
          testId="canonical-page-surface"
          ariaLabel={`Rendered page ${page.pageNumber}`}
        />
      </div>

      {showPreviousPageOverlay && previousPage && (
        <div
          className="previous-page-overlay"
          aria-hidden="true"
          style={{ opacity: previousPageOpacity }}
        >
          <CanonicalPageRenderer
            page={previousPage}
            canvasSize={canvasSize}
            className="previous-canonical-page"
          />
        </div>
      )}

      <span id={keyboardHelpId} className="canvas-screen-reader-only">
        Select an element, then use the Arrow keys to move it one canvas unit or Shift plus an Arrow key to move it ten units. Press Enter on a text box to edit its text and Escape to return to moving it.
      </span>
      <span className="canvas-screen-reader-only" aria-live="polite" aria-atomic="true">
        {assistAnnouncement}
      </span>

      {selectedCanvasElement && <div
        className="canvas-assist-toolbar"
        data-testid="canvas-assist-toolbar"
        data-selected-element={selectedElement || ''}
        data-selection-count={selectionCount}
        data-page-element-count={pageElementCount}
        role="toolbar"
        aria-label="Canvas alignment and snapping"
        aria-orientation="horizontal"
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={`canvas-assist-button snap-toggle ${snappingEnabled ? 'active' : ''}`}
          data-testid="snap-toggle"
          aria-label={`Smart snapping ${snappingEnabled ? 'on' : 'off'}`}
          aria-pressed={snappingEnabled}
          title="Toggle smart snapping to page and element guides"
          onClick={() => {
            const enabled = !snappingEnabled;
            setSnappingEnabled(enabled);
            if (!enabled) setSmartGuides([]);
            setAssistAnnouncement(`Smart snapping ${enabled ? 'on' : 'off'}.`);
          }}
        >
          <span aria-hidden="true">⌁</span>
          <span>Snap</span>
        </button>
        <span className="canvas-assist-divider" role="separator" aria-orientation="vertical" />
        {ALIGNMENT_CONTROLS.map(control => (
          <button
            key={control.alignment}
            type="button"
            className="canvas-assist-button canvas-align-button"
            data-testid={control.testId}
            aria-label={control.label}
            title={control.label}
            disabled={!selectedCanvasElement}
            onClick={() => alignSelectedElement(control.alignment)}
          >
            <span aria-hidden="true">{control.icon}</span>
          </button>
        ))}
        <span className="canvas-assist-divider" role="separator" aria-orientation="vertical" />
        <button
          type="button"
          className="canvas-assist-button canvas-distribute-button"
          data-testid="distribute-horizontal"
          aria-label={`Distribute all page elements horizontally. ${distributionHelp}`}
          title={distributionHelp}
          aria-describedby={`${keyboardHelpId}-distribution`}
          disabled={!canDistribute}
          onClick={() => distributePageElements('horizontal')}
        >
          <span aria-hidden="true">⇹</span>
        </button>
        <button
          type="button"
          className="canvas-assist-button canvas-distribute-button"
          data-testid="distribute-vertical"
          aria-label={`Distribute all page elements vertically. ${distributionHelp}`}
          title={distributionHelp}
          aria-describedby={`${keyboardHelpId}-distribution`}
          disabled={!canDistribute}
          onClick={() => distributePageElements('vertical')}
        >
          <span aria-hidden="true">⇳</span>
        </button>
        <span id={`${keyboardHelpId}-distribution`} className="canvas-screen-reader-only">
          {distributionHelp}
        </span>
      </div>}

      <div className="editor-interaction-layer">
        {page.imageElements.map(image => (
          <div
            key={image.id}
            className={`editor-interaction-element image-interaction ${selectedElement === image.id ? 'selected' : ''}`}
            style={interactionStyle(image)}
            role="button"
            tabIndex={0}
            data-testid="canvas-image-element"
            data-element-id={image.id}
            data-element-type="image"
            aria-label={getImageAccessibleName(image)}
            aria-describedby={keyboardHelpId}
            aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown"
            aria-pressed={selectedElement === image.id}
            onFocus={() => onSelectElement(image.id)}
            onMouseDown={event => startMove(event, image, 'image')}
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onSelectElement(image.id);
              setAssistAnnouncement(`${getImageAccessibleName(image)} selected.`);
            }}
          >
            {resizeHandles(image, 'image')}
          </div>
        ))}
        {page.textElements.map(text => (
          <div
            key={text.id}
            className={`editor-interaction-element text-interaction ${selectedElement === text.id ? 'selected' : ''}`}
            style={{
              ...interactionStyle(text),
              height: text.height + (text.styleId?.includes('bubble') ? 30 : 0)
            }}
            role="group"
            tabIndex={0}
            data-testid="canvas-text-element"
            data-element-id={text.id}
            data-element-type="text"
            aria-label={String(text.content || '').trim() ? `Text box: ${text.content.slice(0, 80)}` : 'Empty text box'}
            aria-describedby={keyboardHelpId}
            aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Enter"
            onFocus={() => onSelectElement(text.id)}
            onMouseDown={event => startMove(event, text, 'text')}
            onKeyDown={event => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.querySelector<HTMLTextAreaElement>('.canonical-text-editor')?.focus();
              setAssistAnnouncement('Editing story text. Press Escape to return to moving the text box.');
            }}
          >
            {textEditor(text)}
            {resizeHandles(text, 'text')}
          </div>
        ))}
      </div>

      {(showGrid || smartGuides.length > 0) && (
        <svg
          className="canvas-guides"
          data-testid="canvas-guides"
          aria-hidden="true"
          viewBox={`0 0 ${sizeConfig.width} ${sizeConfig.height}`}
        >
          {showGrid && (
            <g data-testid="safe-zone-guides">
              <defs>
                <pattern id={gridPatternId} width="50" height="50" patternUnits="userSpaceOnUse">
                  <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(100,100,255,.3)" strokeWidth=".5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#${gridPatternId})`} />
              <rect
                className="safe-area-guide"
                data-safe-margin={safeMargin}
                x={safeMargin}
                y={safeMargin}
                width={Math.max(0, sizeConfig.width - safeMargin * 2)}
                height={Math.max(0, sizeConfig.height - safeMargin * 2)}
              />
              <line className="center-guide" x1={sizeConfig.width / 2} y1="0" x2={sizeConfig.width / 2} y2={sizeConfig.height} />
              <line className="center-guide" x1="0" y1={sizeConfig.height / 2} x2={sizeConfig.width} y2={sizeConfig.height / 2} />
            </g>
          )}
          {smartGuides.map(guide => guide.axis === 'x' ? (
            <line
              key="smart-guide-x"
              className={`smart-guide smart-guide-x ${guide.source}`}
              data-testid="smart-guide-x"
              data-guide-position={guide.position}
              data-snap-source={guide.source}
              data-target-element-id={guide.targetElementId || ''}
              data-moving-anchor={guide.movingAnchor}
              data-target-anchor={guide.targetAnchor}
              x1={guide.position}
              y1="0"
              x2={guide.position}
              y2={sizeConfig.height}
            />
          ) : (
            <line
              key="smart-guide-y"
              className={`smart-guide smart-guide-y ${guide.source}`}
              data-testid="smart-guide-y"
              data-guide-position={guide.position}
              data-snap-source={guide.source}
              data-target-element-id={guide.targetElementId || ''}
              data-moving-anchor={guide.movingAnchor}
              data-target-anchor={guide.targetAnchor}
              x1="0"
              y1={guide.position}
              x2={sizeConfig.width}
              y2={guide.position}
            />
          ))}
        </svg>
      )}
    </div>
  );
};

export default Canvas;
